import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, CalendarDays, ExternalLink, Search, ScanLine } from 'lucide-react';
import api from '@/utils/api';
import RatingTrendSparkline from '@/components/RatingTrendSparkline';

const STATE_COLORS = {
  draft: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  review_required: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  scheduled: 'bg-blue-50 text-blue-800 border-blue-200',
  published: 'bg-success-50 text-success-700 border-success-200',
  sold_out: 'bg-accent-50 text-accent-700 border-accent-200',
  completed: 'bg-neutral-100 text-neutral-500 border-neutral-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
  archived: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const EVENT_TYPES = [
  'local_gathering', 'dinner', 'lounge_event', 'community_meetup',
  'workshop', 'activity', 'retreat', 'domestic_travel',
  'international_travel', 'private', 'other',
];

const AdminEventsListPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  // Bundle 151b - one aggregate call for badges. Shape:
  // `{ [event_id]: count }` for rows in
  // (user_marked_paid, instructions_viewed,
  //  auto_verified_pending_review).
  const [reconCounts, setReconCounts] = useState({});
  const navigate = useNavigate();

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/events', { params: { q: q || undefined } });
      setEvents(data.events || []);
    } finally { setLoading(false); }
  };

  const fetchReconCounts = async () => {
    try {
      const { data } = await api.get(
        '/admin/reconciliation/pending-counts');
      setReconCounts(data?.counts || {});
    } catch (e) {
      // Non-critical; the badge just does not render.
      setReconCounts({});
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchReconCounts();
  }, []); // eslint-disable-line

  return (
    <div className="p-8 space-y-6 max-w-6xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Events</h1>
          <p className="text-neutral-600 text-sm">Create, edit, and publish Dostination events.</p>
          <div className="mt-3">
            {/* Bundle 78 Task 3 - avg rating trend across the last 3 completed events. */}
            <RatingTrendSparkline n={3} />
          </div>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-primary-900 font-semibold shadow-gold">
              <Plus className="h-4 w-4 mr-2" /> New event
            </Button>
          </DialogTrigger>
          <CreateEventDialog
            onCreated={(id) => { setCreateOpen(false); navigate(`/admin/events/${id}/edit`); }}
          />
        </Dialog>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            placeholder="Search title or slug…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchEvents()}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={fetchEvents}>Search</Button>
      </div>

      <Card className="border shadow-soft">
        <CardContent className="p-0 divide-y divide-neutral-100">
          {loading ? (
            <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>
          ) : events.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">No events yet. Create your first one.</div>
          ) : (
            events.map((e) => (
              <div
                key={e.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/events/${e.id}/edit`)}
                onKeyDown={(ev) => { if (ev.key === 'Enter') navigate(`/admin/events/${e.id}/edit`); }}
                className="flex items-center gap-4 p-5 hover:bg-neutral-50 transition-colors cursor-pointer"
                data-testid={`admin-events-row-${e.id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center text-white shadow-soft flex-shrink-0">
                  <CalendarDays className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-semibold text-primary-900 truncate">{e.title}</div>
                  <div className="text-xs text-neutral-500 flex items-center gap-2 mt-1">
                    <span className="font-mono">{e.slug}</span>
                    {e.state === 'published' && (
                      <a
                        href={`/events/${e.slug}`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(ev) => ev.stopPropagation()}
                        className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-900"
                      >
                        Preview <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    <Link
                      to={`/admin/events/${e.id}/check-in`}
                      onClick={(ev) => ev.stopPropagation()}
                      data-testid={`admin-events-row-scan-${e.id}`}
                      className="inline-flex items-center gap-1 text-secondary-700 hover:text-secondary-900"
                    >
                      Scan <ScanLine className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
                <Badge variant="outline" className={`${STATE_COLORS[e.state] || STATE_COLORS.draft} font-medium capitalize`}>
                  {(e.state || 'draft').replace(/_/g, ' ')}
                </Badge>
                {/* Bundle 151b - pending-reconciliation badge. Clickable,
                    routes to the reconciliation queue for that event. */}
                {(reconCounts[e.id] || 0) > 0 && (
                  <Link
                    to={`/admin/events/${e.id}/reconciliation`}
                    onClick={(ev) => ev.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2.5 py-1 shadow-soft"
                    data-testid={`admin-events-recon-badge-${e.id}`}
                    aria-label={`${reconCounts[e.id]} pending reconciliation for ${e.title}`}
                  >
                    {reconCounts[e.id]} pending reconciliation
                  </Link>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CreateEventDialog = ({ onCreated }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('lounge_event');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    setSaving(true);
    try {
      const { data } = await api.post('/admin/events', {
        basics: { title: title.trim(), event_type: type },
      });
      onCreated(data.event_id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-primary-900">New event</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="e.g. Summer of Dosti - Dallas" className="mt-1" />
          <p className="text-xs text-neutral-500 mt-1">Slug will be generated from the title - you can edit it later.</p>
        </div>
        <div>
          <Label>Event type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {error && <div className="text-sm text-accent-700">{error}</div>}
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={!title.trim() || saving} className="bg-primary-800 hover:bg-primary-900 text-white">
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default AdminEventsListPage;
